import React, { useRef, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useTheme,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  History,
  KeyboardArrowDown,
  Chat,
  Edit,
  Delete,
} from '@mui/icons-material';
import { useEdvisor } from '../../contexts/EdvisorContext';
import RenameThreadDialog from './RenameThreadDialog';
import DeleteThreadDialog from './DeleteThreadDialog';

interface ChatHistoryDropdownProps {
  disabled?: boolean;
}

const ChatHistoryDropdown: React.FC<ChatHistoryDropdownProps> = ({ disabled = false }) => {
  const theme = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const {
    chatHistory,
    showChatHistory,
    toggleChatHistory,
    selectChatThread,
    renameThread,
    deleteThread,
  } = useEdvisor();

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [threadToRename, setThreadToRename] = useState<{ id: string; title: string } | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<{ id: string; title: string } | null>(null);

  const handleThreadSelect = async (threadId: string) => {
    await selectChatThread(threadId);
  };

  const handleRenameClick = (e: React.MouseEvent, threadId: string, currentTitle: string) => {
    e.stopPropagation(); // Prevent menu item selection
    setThreadToRename({ id: threadId, title: currentTitle });
    setRenameDialogOpen(true);
  };

  const handleRename = async (newTitle: string) => {
    if (!threadToRename) return;
    await renameThread(threadToRename.id, newTitle);
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setThreadToRename(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, threadId: string, currentTitle: string) => {
    e.stopPropagation(); // Prevent menu item selection
    setThreadToDelete({ id: threadId, title: currentTitle });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!threadToDelete) return;
    await deleteThread(threadToDelete.id);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setThreadToDelete(null);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <>
      <Button
        ref={buttonRef}
        variant="outlined"
        size="small"
        startIcon={<History />}
        endIcon={<KeyboardArrowDown />}
        onClick={toggleChatHistory}
        disabled={disabled}
        sx={{
          borderRadius: 2,
          minWidth: 'auto',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        CHAT HISTORY
      </Button>

      <Menu
        anchorEl={showChatHistory ? buttonRef.current : null}
        open={showChatHistory}
        onClose={toggleChatHistory}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxWidth: 400,
            maxHeight: 400,
            overflow: 'auto',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {chatHistory.length === 0 ? (
          <MenuItem disabled>
            <Box sx={{ textAlign: 'center', py: 2, width: '100%' }}>
              <Chat sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No chat history yet
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Start a conversation to see it here
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Previous Conversations
              </Typography>
            </Box>
            <Divider />
            {chatHistory.map((thread, index) => (
              <MenuItem
                key={thread.threadId}
                onClick={() => handleThreadSelect(thread.threadId)}
                sx={{
                  backgroundColor: thread.isActive ? theme.palette.primary.light : 'transparent',
                  '&:hover': {
                    backgroundColor: thread.isActive 
                      ? theme.palette.primary.light 
                      : theme.palette.action.hover,
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  },
                  position: 'relative',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Chat 
                    fontSize="small" 
                    sx={{ 
                      color: thread.isActive ? 'white' : 'action.active',
                      opacity: thread.isActive ? 0.9 : 1,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: thread.isActive ? 600 : 400,
                        color: thread.isActive ? 'white' : 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pr: 4, // Add padding for rename button
                      }}
                    >
                      {thread.title}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        color={thread.isActive ? 'white' : 'text.secondary'}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          mr: 1,
                          opacity: thread.isActive ? 0.9 : 1,
                        }}
                      >
                        {thread.lastMessage}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={thread.isActive ? 'white' : 'text.disabled'}
                        sx={{ 
                          flexShrink: 0,
                          opacity: thread.isActive ? 0.8 : 1,
                        }}
                      >
                        {formatTimestamp(thread.timestamp)}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ 
                  position: 'absolute', 
                  right: 4, 
                  top: 8,
                  display: 'flex',
                  gap: 0.5,
                }}>
                  <Tooltip title="Rename thread">
                    <IconButton
                      size="small"
                      onClick={(e) => handleRenameClick(e, thread.threadId, thread.title)}
                      sx={{
                        opacity: 0.7,
                        padding: '2px',
                        minWidth: 'auto',
                        width: '20px',
                        height: '20px',
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: thread.isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Edit sx={{ 
                        fontSize: '14px',
                        color: thread.isActive ? 'white' : 'inherit',
                      }} />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete thread">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(e, thread.threadId, thread.title)}
                      sx={{
                        opacity: 0.7,
                        padding: '2px',
                        minWidth: 'auto',
                        width: '20px',
                        height: '20px',
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: thread.isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Delete sx={{ 
                        fontSize: '14px',
                        color: thread.isActive ? 'white' : 'inherit',
                      }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
      
      <RenameThreadDialog
        open={renameDialogOpen}
        onClose={handleRenameDialogClose}
        onRename={handleRename}
        currentTitle={threadToRename?.title || ''}
        threadId={threadToRename?.id || ''}
      />
      
      <DeleteThreadDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onDelete={handleDelete}
        threadTitle={threadToDelete?.title || ''}
      />
    </>
  );
};

export default ChatHistoryDropdown;
